/**
 * useToaster Composable
 *
 * Reusable toast notification wrapper around PrimeVue's Toast service.
 * Provides a simplified API with severity shortcuts, message, actions, and timeout.
 *
 * Prerequisites:
 *  - PrimeVue ToastService plugin must be registered in the app (app.use(ToastService))
 *  - <AppToast /> (or PrimeVue <Toast />) must be placed in the root layout
 *
 * @example
 * ```ts
 * const toast = useToaster();
 *
 * toast.success('Patient created successfully');
 * toast.danger('Failed to save', { detail: 'Network error', life: 8000, position: 'bottom-right' });
 * toast.warn('Session expiring soon');
 * toast.info('New update available', {
 *   detail: 'Click to refresh',
 *   actions: [{ label: 'Refresh', onClick: () => location.reload() }],
 * });
 * ```
 */
import { useToast } from 'primevue/usetoast';
import { reactive } from 'vue';

export type ToastSeverity = 'success' | 'danger' | 'warn' | 'info';

export type ToastAction = {
  /** Button label */
  label: string;
  /** Callback when the action is clicked */
  onClick: () => void;
};

export type ToastPosition = 'top-right' | 'top-left' | 'top-center' | 'bottom-right' | 'bottom-left' | 'bottom-center' | 'center';

export type ToasterOptions = {
  /** Secondary descriptive text shown below the summary */
  detail?: string;
  /** Duration in ms before auto-dismiss. Defaults to 3000. Set 0 for sticky. */
  life?: number;
  /** Whether the toast can be manually closed. Defaults to true. */
  closable?: boolean;
  /** Action buttons rendered inside the toast */
  actions?: ToastAction[];
  /** PrimeVue toast group for scoped rendering */
  group?: string;
  /** Position on screen. Defaults to 'top-center'. */
  position?: ToastPosition;
};

/** Maps the public variant name to PrimeVue's internal severity value */
const severityMap: Record<ToastSeverity, string> = {
  success: 'success',
  info: 'info',
  warn: 'warn',
  danger: 'error',
};

const DEFAULT_LIFE = 5000;

/**
 * Shared reactive store for toast action metadata.
 * Keyed by a unique toast ID so the AppToast template can look up actions.
 */
export const toastActionsStore = reactive<Record<string, ToastAction[]>>({});

let toastIdCounter = 0;

export function useToaster() {
  const toast = useToast();

  /**
   * Show a toast notification.
   *
   * @param severity - Visual variant: 'success' | 'danger' | 'warn' | 'info'
   * @param summary  - Primary message text
   * @param options  - Optional detail, life, closable, actions, group, position
   */
  function show(severity: ToastSeverity, summary: string, options: ToasterOptions = {}) {
    const {
      detail,
      life = DEFAULT_LIFE,
      closable = true,
      actions,
      group,
    } = options;

    const toastId = `toast-${++toastIdCounter}`;

    // Store actions in the shared reactive store for AppToast to pick up
    if (actions?.length) {
      toastActionsStore[toastId] = actions;
    }

    toast.add({
      severity: severityMap[severity],
      summary,
      detail: actions?.length ? `${toastId}::${detail ?? ''}` : detail,
      life: life === 0 ? undefined : life,
      closable,
      group,
    });
  }

  /** Show a success toast */
  function success(summary: string, options?: ToasterOptions) {
    show('success', summary, options);
  }

  /** Show a danger/error toast */
  function danger(summary: string, options?: ToasterOptions) {
    show('danger', summary, options);
  }

  /** Show a warning toast */
  function warn(summary: string, options?: ToasterOptions) {
    show('warn', summary, options);
  }

  /** Show an info toast */
  function info(summary: string, options?: ToasterOptions) {
    show('info', summary, options);
  }

  /** Remove all toasts */
  function clear() {
    toast.removeAllGroups();
  }

  return {
    show,
    success,
    danger,
    warn,
    info,
    clear,
  };
}
